import { useEffect, useState } from 'react'
import { dialog } from '@carrent/platform'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@carrent/core'
import { tasksApi }     from '@carrent/api'
import {
  priorityMeta,
  formatDuration,
  isStaff,
  describeDue,
  DUE_PRESETS,
  toDateTimeLocal,
  fromDateTimeLocal,
} from '@carrent/domain'
import { StatusBadge, DueBadge, DueField, Icon } from '@carrent/ui'
import { UserPicker }   from './UserPicker'

/** Тон статуса для полоски подзадачи. Совпадает с TASK_STATUS_META. */
const STATUS_TONES = {
  open: 'info',
  in_progress: 'warning',
  on_review: 'secondary',
  completed: 'success',
  cancelled: 'neutral',
}
const statusTone = (status) => STATUS_TONES[status] || 'neutral'

export default function TaskDetailPage() {
  const { id }   = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [task, setTask]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr]         = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await tasksApi.get(id)
      setTask(data.data)
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка загрузки')
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [id])

  if (loading) return <div className="page text-muted text-center" style={{ padding: 24 }}>Загрузка...</div>
  if (err)     return <div className="page"><div className="alert alert--error">{err}</div></div>
  if (!task)   return null

  const pr     = priorityMeta(task.priority)

  const isCreator    = task.created_by?.id === user.id
  const isAssignee   = task.assigned_to?.id === user.id
  const isManager    = isStaff(user)
  const isAdmin      = user?.role === 'admin'
  const canEdit      = isManager && (isCreator || isAdmin || task.region?.id) // менеджер своего региона
  const canCancel    = isCreator || isAdmin
  const canDelete    = isAdmin   // удалять — только админ

  const action = async (fn, ok = 'Готово') => {
    setErr('')
    try { await fn(); await load() }
    catch (e) { setErr(e.response?.data?.message || 'Ошибка') }
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <button onClick={() => navigate(-1)} className="btn-icon">‹</button>
        <span className="text-muted" style={{ fontSize: '0.78rem' }}>#{task.id}</span>
        <h2 style={{ margin: 0, flex: 1, fontSize: '1.05rem' }}>{task.title}</h2>
        <StatusBadge status={task.status} />
      </div>

      {/* Ссылка на родителя — задача является подзадачей чего-то */}
      {task.parent_id && (
        <div onClick={() => navigate(`/tasks/${task.parent_id}`)}
             className="card" style={{
               padding: 8, marginBottom: 'var(--gap)',
               background: 'var(--bg)', cursor: 'pointer',
               borderLeft: '3px solid var(--accent)',
               display: 'flex', alignItems: 'center', gap: 6,
             }}>
          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
            <Icon name="chevronLeft" size={12} /> Часть задачи #{task.parent_id}
          </span>
        </div>
      )}

      {err && <div className="alert alert--error" style={{ marginBottom: 'var(--gap)' }}>{err}</div>}

      {/* Шапка */}
      <div className="card" style={{ marginBottom: 'var(--gap)' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          <span className={`badge badge--${pr.tone}`}>{pr.label}</span>
          <DueBadge due={describeDue(task)} />
          {task.region && (
            <span className="badge badge--info">
              <Icon name="map" size={11} />
              {task.region.code}
            </span>
          )}
        </div>

        {task.description && (
          <p style={{ marginTop: 8, marginBottom: 8, whiteSpace: 'pre-wrap' }}>{task.description}</p>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.82rem' }}>
          <Field label="Создал"      value={task.created_by?.full_name} />
          <Field label="Исполнитель" value={task.assigned_to?.full_name || '—'} />
          {task.car && <Field label="Машина" value={`${task.car.brand} ${task.car.model} (${task.car.plate})`} />}
          {task.related_user && <Field label="Связан" value={task.related_user.full_name} />}
          {task.due_at && <Field label="Срок" value={describeDue(task)?.exact} />}
          <Field label="Время в работе" value={formatDuration(task.time_in_work_seconds)} />
          {task.started_at && <Field label="Начата" value={new Date(task.started_at).toLocaleString('ru')} />}
          {task.completed_at && <Field label="Завершена" value={new Date(task.completed_at).toLocaleString('ru')} />}
          <Field label="Создана" value={new Date(task.created_at).toLocaleString('ru')} />
        </div>
      </div>

      {/* Статусные действия */}
      <StatusActions
        task={task}
        canManage={canEdit || isAssignee}
        canCancel={canCancel}
        onAction={action}
      />

      {/* Согласующие */}
      <ApproversBlock task={task} canEdit={canEdit} onChange={load} currentUserId={user.id} />

      {/* Подзадачи */}
      <SubtasksBlock task={task} canCreate={isManager} />

      {/* Комментарии */}
      <CommentsBlock task={task} onChange={load} />

      {/* Действия удаления */}
      {canDelete && (
        <button className="btn btn--danger" style={{ marginTop: 'var(--gap)' }}
          onClick={async () => {
            if (!await dialog.confirm('Удалить задачу?')) return
            tasksApi.delete(task.id).then(() => navigate('/tasks'))
          }}>
          Удалить задачу
        </button>
      )}
    </div>
  )
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-muted" style={{ fontSize: '0.7rem' }}>{label}</div>
      <div>{value}</div>
    </div>
  )
}

function StatusActions({ task, canManage, canCancel, onAction }) {
  if (!canManage && !canCancel) return null
  if (task.status === 'completed' || task.status === 'cancelled') {
    return canCancel ? (
      <button className="btn btn--outline" style={{ marginBottom: 'var(--gap)' }}
        onClick={() => onAction(() => tasksApi.reopen(task.id))}>
        Переоткрыть
      </button>
    ) : null
  }

  const buttons = []
  if (task.status === 'open' && canManage) {
    buttons.push({ label: 'Взять в работу', primary: true,
      fn: () => tasksApi.start(task.id) })
  }
  if (task.status === 'in_progress' && canManage) {
    buttons.push({ label: 'На проверку',
      fn: () => tasksApi.review(task.id) })
    buttons.push({ label: 'Завершить', primary: true,
      fn: () => tasksApi.complete(task.id) })
  }
  if (task.status === 'on_review' && canManage) {
    buttons.push({ label: 'Завершить', primary: true,
      fn: () => tasksApi.complete(task.id) })
  }
  if (canCancel) {
    buttons.push({ label: 'Отменить', danger: true,
      fn: async () => {
        if (await dialog.confirm('Отменить задачу?')) return tasksApi.cancel(task.id)
        return undefined
      } })
  }

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--gap)' }}>
      {buttons.map((b, i) => (
        <button key={i}
          className={`btn ${b.primary ? 'btn--primary' : b.danger ? 'btn--danger' : 'btn--outline'}`}
          style={{ flex: 1, minWidth: 120 }}
          onClick={() => onAction(b.fn)}>
          {b.label}
        </button>
      ))}
    </div>
  )
}

function ApproversBlock({ task, canEdit, onChange, currentUserId }) {
  const [adding, setAdding] = useState(false)

  const add = async user => {
    await tasksApi.addApprover(task.id, user.id)
    setAdding(false); onChange()
  }
  const remove = async aid => {
    if (!await dialog.confirm('Удалить согласующего?')) return
    await tasksApi.removeApprover(task.id, aid); onChange()
  }
  const decide = async (aid, status) => {
    const comment = status === 'rejected' ? (await dialog.prompt('Причина отклонения:')) || '' : ''
    await tasksApi.decideApprover(task.id, aid, status, comment); onChange()
  }

  return (
    <div className="card" style={{ marginBottom: 'var(--gap)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: '0.9rem' }}>Согласующие ({task.approvers.length})</strong>
        {canEdit && (
          <button className="chip" onClick={() => setAdding(!adding)}>
            {adding ? '× закрыть' : '+ добавить'}
          </button>
        )}
      </div>

      {adding && (
        <div style={{ marginBottom: 8 }}>
          <UserPicker onChange={add} placeholder="Найти сотрудника..." role="manager" />
        </div>
      )}

      {task.approvers.length === 0 ? (
        <div className="text-muted" style={{ fontSize: '0.8rem' }}>Нет согласующих</div>
      ) : (
        <div className="flex-col" style={{ gap: 4 }}>
          {task.approvers.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
              padding: 6, background: 'var(--bg)', borderRadius: 'var(--r-sm)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem' }}>{a.user.full_name}</div>
                {a.comment && <div className="text-muted" style={{ fontSize: '0.72rem' }}>«{a.comment}»</div>}
              </div>
              {/* Тон берётся из токенов, а не из хексов: три цвета
                  решения были заданы мимо палитры и не совпадали
                  ни с одним статусом в остальной системе. */}
              <span className={`badge badge--${
                a.status === 'approved' ? 'success' : a.status === 'rejected' ? 'danger' : 'neutral'
              }`}>{a.status_label}</span>

              {a.user.id === currentUserId && a.status === 'pending' && (
                <>
                  <button className="btn-icon" onClick={() => decide(a.id, 'approved')}
                    title="Согласовать" style={{ color: 'var(--success)' }}>
                    <Icon name="check" size={16} />
                  </button>
                  <button className="btn-icon" onClick={() => decide(a.id, 'rejected')}
                    title="Отклонить" style={{ color: 'var(--danger)' }}>
                    <Icon name="close" size={16} />
                  </button>
                </>
              )}
              {canEdit && (
                <button className="btn-icon" onClick={() => remove(a.id)} title="Удалить">×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SubtasksBlock({ task, canCreate }) {
  const navigate = useNavigate()
  const subs = task.subtasks || []
  if (subs.length === 0 && !canCreate) return null

  return (
    <div className="card" style={{ marginBottom: 'var(--gap)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: '0.9rem' }}>📂 Подзадачи ({subs.length})</strong>
        {canCreate && (
          <button className="chip" onClick={() => navigate(`/tasks/new?parent=${task.id}`)}>
            + добавить
          </button>
        )}
      </div>
      {subs.length === 0 ? (
        <div className="text-muted" style={{ fontSize: '0.8rem' }}>Нет подзадач</div>
      ) : (
        <div className="flex-col" style={{ gap: 4 }}>
          {subs.map(s => {
            // Полоска слева кодирует статус через токен, а не через
            // st.color: такого поля в доменных метаданных нет, и
            // граница получалась `3px solid undefined`.
            const due = describeDue(s)
            return (
              <div key={s.id} onClick={() => navigate(`/tasks/${s.id}`)}
                className={`subtask subtask--${statusTone(s.status)}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.85rem' }}>
                    <span className="text-muted" style={{ fontSize: '0.72rem', marginRight: 6 }}>#{s.id}</span>
                    {s.title}
                  </div>
                  {s.assigned_to && (
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      {s.assigned_to.full_name}
                    </div>
                  )}
                </div>
                <DueBadge due={due} compact />
                <StatusBadge status={s.status} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function CommentsBlock({ task, onChange }) {
  const comments = task.comments || []
  const [text, setText] = useState('')
  const [files, setFiles] = useState([])
  const [mentions, setMentions] = useState([])
  const [showMention, setShowMention] = useState(false)
  const [sending, setSending] = useState(false)

  const send = async () => {
    if (!text.trim() && files.length === 0) return
    setSending(true)
    try {
      const fd = new FormData()
      fd.append('text', text)
      mentions.forEach(m => fd.append('mention_ids', m.id))
      // Mention IDs через запятую
      if (mentions.length) fd.set('mention_ids', mentions.map(m => m.id).join(','))
      files.forEach(f => fd.append('files', f))
      await tasksApi.addComment(task.id, fd)
      setText(''); setFiles([]); setMentions([])
      onChange()
    } finally { setSending(false) }
  }

  return (
    <div className="card">
      <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: 8 }}>
        💬 Комментарии ({comments.length})
      </strong>

      <div className="flex-col" style={{ gap: 8, marginBottom: 'var(--gap)' }}>
        {comments.length === 0 ? (
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>Пока нет комментариев</div>
        ) : comments.map(c => (
          <div key={c.id} style={{ padding: 8, background: 'var(--bg)', borderRadius: 'var(--r-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <strong style={{ fontSize: '0.82rem' }}>{c.author.full_name}</strong>
              <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                {new Date(c.created_at).toLocaleString('ru')}
              </span>
            </div>
            {c.text && (
              <p style={{ margin: '4px 0', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>{c.text}</p>
            )}
            {c.mentions?.length > 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--accent)' }}>
                {c.mentions.map(m => `@${m.full_name}`).join(', ')}
              </div>
            )}
            {c.attachments?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {c.attachments.map(a => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
                    <img src={a.url} alt=""
                      style={{ width: 60, height: 60, objectFit: 'cover',
                        borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Композер */}
      <div className="flex-col" style={{ gap: 4 }}>
        <textarea className="input" rows={2} placeholder="Написать комментарий..."
          value={text} onChange={e => setText(e.target.value)}
          style={{ resize: 'vertical' }} />

        {mentions.length > 0 && (
          <div style={{ fontSize: '0.78rem', color: 'var(--accent)' }}>
            Упомянуты: {mentions.map(m => m.full_name).join(', ')}{' '}
            <button onClick={() => setMentions([])} className="btn-icon">×</button>
          </div>
        )}

        {showMention && (
          <UserPicker multi selected={mentions} onChange={setMentions}
            placeholder="Упомянуть @..." />
        )}

        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <label className="chip" style={{ cursor: 'pointer' }}>
            📎 Файл
            <input type="file" multiple accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={e => setFiles([...files, ...e.target.files])} />
          </label>
          <button className="chip" onClick={() => setShowMention(!showMention)}>@ Упомянуть</button>
          {files.length > 0 && (
            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
              {files.length} файл{files.length === 1 ? '' : 'а'}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn btn--primary"
            onClick={send} disabled={sending || (!text.trim() && files.length === 0)}
            style={{ padding: '6px 16px' }}>
            {sending ? '...' : 'Отправить'}
          </button>
        </div>
      </div>
    </div>
  )
}
